import React, { useEffect, useRef, useState } from 'react';
import { COPILOT_PANEL_WIDTH } from '@components/Copilot/constants';
import { AvMenu, BoxResizable, CopyToClipboard, CopyType } from '@fabric-ui/components';
import { Maximize, Minimize, X } from '@fabric-ui/icons';
import { APPBAR_HEIGHT, flex } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import {
  Box,
  ClickAwayListener,
  Divider,
  Drawer,
  drawerClasses,
  DrawerProps,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  StackProps,
  SxProps,
  Typography,
} from '@mui/material';
import { useAvCopilotContext } from '../context/AvCopilotProvider';

const closeButton = <X style={{ width: 22, height: 22 }} />;
const maximizeIcon = <Maximize style={{ width: 16, height: 16, transform: 'rotate(90deg)' }} />;
const minimizeIcon = <Minimize style={{ width: 16, height: 16, transform: 'rotate(90deg)' }} />;

const forceAllClickAwayHandlers = () => document.body.click();

export interface AvSidePanelProps extends Pick<DrawerProps, 'disableAutoFocus'> {
  children?: React.ReactNode;
  storageKey: string;
  maxWidthOffset?: number;
  topNavRightComponent?: React.ReactElement;
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  defaultHeight?: number;
  defaultWidth?: number;
  bottomOffset?: number;
  onClose?: () => void;
  isOpen?: boolean;
  footerComponent?: React.ReactNode;
  headComponent?: React.ReactNode;
  showLoader?: boolean;
  anchor?: 'right' | 'left' | 'bottom';
  menuOptions?: {
    title: string;
    onClick(...args: unknown[]): unknown;
  }[];
  maximized?: boolean;
  setMaximized?: (value: boolean) => void;
  allowMaximaize?: boolean;
  showHeaderDivider?: boolean;
  contentContainerProps?: StackProps;
}
const AvSidePanel: React.FC<AvSidePanelProps> = ({
  children,
  storageKey,
  maxWidthOffset = 0,
  topNavRightComponent,
  maximized: maximizedControlled,
  setMaximized: setMaximizedControlled,
  minWidth = 660,
  maxWidth = 2000,
  minHeight,
  maxHeight,
  defaultHeight,
  defaultWidth,
  bottomOffset = 0,
  onClose = noop,
  isOpen = false,
  footerComponent,
  headComponent,
  showLoader,
  anchor = 'right',
  menuOptions,
  allowMaximaize = true,
  showHeaderDivider = true,
  disableAutoFocus = true,
  contentContainerProps,
}) => {
  const { isOpen: isCopilotOpen } = useAvCopilotContext();
  const headerComponentActionsRef = useRef<HTMLDivElement>(null);
  const [maximizedInner, setMaximizedInner] = useState(false);
  const headerActionsElWidth = headerComponentActionsRef?.current?.clientWidth || 0;
  const isMaximizedControlled = maximizedControlled !== undefined;
  const maximized = isMaximizedControlled ? maximizedControlled : maximizedInner;
  const setMaximized = isMaximizedControlled ? setMaximizedControlled : setMaximizedInner;
  const isVertical = anchor === 'bottom';

  const onClickAwayHandler = (e: any) => {
    if (!document.body.contains(e.target)) {
      return;
    }

    const isClickInsideLink = e.target.closest('a');
    const isClickInsideDialog = e.target.closest('[role="dialog"]');
    const isClickInsideDrawer = e.target.closest('[role="presentation"]');
    const isClickInsideErrorsSnackbar = e.target.closest('.notistack-SnackbarContainer');
    const isBodyClick = e.target === document.body;

    if (!isClickInsideErrorsSnackbar && !isClickInsideLink && !isClickInsideDialog && !isClickInsideDrawer && !isBodyClick) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      forceAllClickAwayHandlers();
    }
  }, [isOpen]);

  const drawerSx: SxProps = {
    [`&.${drawerClasses.root}`]: {
      ...(isVertical
        ? {
            height: 'fit-content',
            top: 'auto',
          }
        : {
            right: 0,
            left: 'auto',
            width: 'fit-content',
            transform: isCopilotOpen ? `translateX(-${COPILOT_PANEL_WIDTH}px)` : 'translateX(0)',
          }),
    },
    [`.${drawerClasses.paper}`]: {
      overflow: 'inherit',
      ...(isVertical && {
        left: 'var(--side-bar-width)',
        width: `calc(100% - var(--side-bar-width) - ${isCopilotOpen ? COPILOT_PANEL_WIDTH : 0}px)`,
        bottom: bottomOffset,
      }),
    },
  };

  const boxResizableSx: SxProps = {
    ...(isVertical
      ? {
          width: '100%',
          height: maximized ? `calc(100vh - ${APPBAR_HEIGHT}px)` : undefined,
          maxHeight: `calc(100vh - ${APPBAR_HEIGHT}px - ${bottomOffset || 0}px)`,
        }
      : {
          pt: `${APPBAR_HEIGHT}px`,
          width: maximized
            ? isCopilotOpen
              ? `calc(100vw - ${COPILOT_PANEL_WIDTH}px - ${maxWidthOffset}px)`
              : `calc(100vw - ${maxWidthOffset}px)`
            : undefined,
          height: '100%',
          maxWidth: isCopilotOpen ? `calc(100vw - ${COPILOT_PANEL_WIDTH}px - ${maxWidthOffset}px)` : `calc(100vw - ${maxWidthOffset}px)`,
        }),
  };

  return (
    <ClickAwayListener onClickAway={onClickAwayHandler}>
      <Drawer
        anchor={anchor}
        open={isOpen}
        disableAutoFocus={disableAutoFocus}
        disableRestoreFocus
        onClose={() => onClose()}
        hideBackdrop
        sx={drawerSx}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}>
        <BoxResizable
          storageKey={storageKey}
          sx={boxResizableSx}
          anchor={anchor}
          minWidth={minWidth}
          maxWidth={maxWidth}
          minHeight={minHeight}
          maxHeight={maxHeight}
          defaultHeight={defaultHeight}
          defaultWidth={defaultWidth}
          disabled={maximized}>
          <Box
            sx={t => ({
              '--AvSidePanel-padding-x': t.spacing(2.5),
              ...flex.col,
              pt: '20px',
              height: '100%',
              '> *': { px: 'var(--AvSidePanel-padding-x)', flexShrink: 0 },
            })}
            aria-label="side-panel">
            <Box sx={{ ...flex.itemsStart, mb: '20px', width: '100%' }}>
              <Box
                sx={{
                  '.ticket-title-container, .back-breadcrumb-title': { maxWidth: `calc(100% - ${headerActionsElWidth}px)` },
                  width: '100%',
                }}>
                {headComponent}
              </Box>
              <Box ref={headerComponentActionsRef} sx={{ ...flex.row, position: 'absolute', right: 24 }}>
                {topNavRightComponent ?? <div />}
                <Box sx={{ ...flex.row, gap: '10px', ml: '10px' }}>
                  {menuOptions && <AvMenu options={menuOptions} />}
                  <Divider flexItem orientation="vertical" sx={{ height: 24, alignSelf: 'center' }} />
                  <Box sx={{ ...flex.itemsCenter, gap: 1.5 }}>
                    {allowMaximaize && (
                      <IconButton size="small" onClick={() => setMaximized?.(!maximized)}>
                        {maximized ? minimizeIcon : maximizeIcon}
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => onClose()}>
                      {closeButton}
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
            {headComponent && showLoader ? (
              <LinearProgress sx={{ height: '3px', mt: '-2px' }} />
            ) : showHeaderDivider ? (
              <Divider sx={!headComponent ? { pb: '150px' } : undefined} />
            ) : null}
            <Stack {...contentContainerProps} sx={{ gap: 1, overflow: 'auto', flexGrow: 1, flexShrink: 1, ...contentContainerProps?.sx }}>
              {children}
            </Stack>
            {footerComponent && (
              <Box
                sx={{
                  ...flex.justifyEndCenter,
                  gap: 1,
                  '&:not(:empty)': {
                    height: 52,
                    padding: '10px 32px',
                    backgroundColor: 'var(--color-surface-field-default)',
                    filter:
                      'drop-shadow(var(--color-effects-position-none) var(--color-effects-position-none) var(--color-effects-blur-ambient-sm) var(--color-effects-color-default)) drop-shadow(var(--color-effects-position-none) var(--color-effects-position-default-sm) var(--color-effects-blur-offset-sm) var(--color-effects-color-strong))',
                    zIndex: 1,
                  },
                }}>
                {footerComponent}
              </Box>
            )}
          </Box>
        </BoxResizable>
      </Drawer>
    </ClickAwayListener>
  );
};
export default AvSidePanel;

const pressKey = code => window.dispatchEvent(new KeyboardEvent('keydown', { code }));

interface NavigationBarProps {
  id: string;
  link?: string;
}
export const NavigationBar: React.FC<NavigationBarProps> = ({ id, link }) => {
  useEffect(() => {
    const registerTicketNav = (event: KeyboardEvent) => {
      if (event.code === 'ArrowLeft') {
        pressKey('ArrowUp');
      } else if (event.code === 'ArrowRight') {
        pressKey('ArrowDown');
      }
    };
    window.addEventListener('keydown', registerTicketNav);

    return () => window.removeEventListener('keydown', registerTicketNav);
  }, []);

  return (
    <Box sx={{ ...flex.itemsCenter }}>
      <Box sx={{ ...flex.center, gap: '4px', minWidth: 80 }}>
        <Typography variant="h7" sx={{ ...flex.row, gap: 1 }}>
          ID / {id ?? <Skeleton width={60} />}
        </Typography>
        <CopyToClipboard copyData={link ?? window.location.href} copyType={CopyType.LINK} />
      </Box>
    </Box>
  );
};

export const AvSidePanelTabsSkeleton = () => (
  <Box sx={{ ...flex.row, height: '100%' }}>
    <Box sx={{ width: 180, gap: 1, pr: '20px' }}>
      <Skeleton width="100%" height={32} />
      <Skeleton width="100%" height={32} />
      <Skeleton width="100%" height={32} />
      <Skeleton width="100%" height={32} />
    </Box>
    <Divider flexItem orientation="vertical" />
    <div />
  </Box>
);
